import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {PaginationResponse} from '@/types.ts'
import {useEffect, useState} from 'react'
import {
    httpGetTreatmentRequests,
    HttpGetTreatmentRequestsOptions
} from '@/features/treatment-requests/services/treatment-requests.http.ts'
import {TreatmentRequestSmall} from '@/features/treatment-requests/types.ts'

const ResponseShape = z.object({data: z.array(TreatmentRequestSmall), meta: PaginationResponse})
type ResponseShape = z.infer<typeof ResponseShape>

export const useTreatmentRequests = (
    params: HttpGetTreatmentRequestsOptions['params'],
    payload: HttpGetTreatmentRequestsOptions['payload'],
    options?: Omit<UseQueryOptions<ResponseShape>, 'queryKey' | 'queryFn'>
) => {
    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: 50,
        currentPage: 1
    })

    const query = useQuery({
        queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, params, payload],
        queryFn: () =>
            parseAxiosPromise({
                axiosPromise: httpGetTreatmentRequests({params, payload}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        ...options
    })

    useEffect(() => {
        if (query.isSuccess) {
            setPersistedPagination(query.data.meta)
        }
    }, [query.data?.data.length])

    return {...query, persistedPagination}
}
